import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { FooterComponent } from '@shared-lib/components/footer/footer.component';
import { HeaderComponent } from '@shared-lib/components/header/header.component';
import { LoginLogoutComponent } from '@shared-lib/components/login-logout/login-logout.component';
import { LoaderComponent } from '@shared-lib/components/loader/loader.component';
import { LoaderService } from '@shared-lib/services/loader.service';
import { SessionTimerService } from '@shared-lib/services/session-timer.service';
import { SideMenuComponent } from '@shared-lib/components/side-menu/side-menu.component';
import { filter, firstValueFrom, Subscription } from 'rxjs';
import { HttpClient, HttpContext } from '@angular/common/http';
import { SKIP_LOADER } from '@shared-lib/services/share-lib.service';

import { environment } from '../environments/environment';
import { getSideMenuItems, SideMenuItem, getMainMenuItems, MainMenuItem } from '@shared-lib/services/side-menu-items';
import { RatingComponent } from '@shared-lib/components/rating/rating.component';
import { DmttReturnTokenValidatorService } from './core/services/dmtt-return-token-validator.service';
import { ClientLoggerService } from './core/services/client-logger.service';
import { TokenService } from '../../../../BL/BL_PORTAL/src/app/Services/auth';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [
    CommonModule,
    RouterOutlet,
    HeaderComponent,
    FooterComponent,
    LoginLogoutComponent,
    LoaderComponent,
    RatingComponent,
    SideMenuComponent,
  ],
  templateUrl: './app.html',
  styleUrls: ['./app.scss'],
})
export class App implements OnInit, OnDestroy {
  readonly menuItems: MainMenuItem[] = getMainMenuItems();
  readonly footerPageMenuItems: MainMenuItem[] = [
    { label: 'Dashboard', route: '/dashboard' },
    { label: 'Taxpayer Service', route: '/tax-payer-services' },
    { label: 'Help & Support', route: '/help' },
  ];
  readonly sideMenuItems: SideMenuItem[] = getSideMenuItems(environment.apiBaseUrl);
  private readonly footerContentRoutes = [
    '/about-us',
    '/privacy-policy',
    '/terms-of-use',
    '/accessibility',
    '/site-map',
    '/help/contact-information',
  ];
  private readonly otbSignupUrl = String((environment as any).otbSignupUrl || 'https://dev.login.onetaxbahamas.gov.bs/signup/dmtt');
  private readonly subscriptions = new Subscription();

  profileName = 'User';
  profileBin = '';
  profileType = 'Business Profile';
  sessionReady = false;
  notificationModule = 'DMTT';
  notificationDashboardRoute = '/returns/track';
  notificationApiBaseUrl = `${String((environment as any).apiBaseUrl || '').trim()}/api/v1`;
  notificationRequestTypes = [
    'Add CE',
    'Change DLE',
    'Group Cancellation',
    'Group Deactivation',
    'Group Reactivation',
    'Leave Group',
    'Profile Details',
    'Registration',
    'Remove CE',
    'Returns',
    'Returns Processing',
    'Review',
  ];

  constructor(
    private readonly loader: LoaderService,
    private readonly tokenValidator: DmttReturnTokenValidatorService,
    private readonly sessionTimer: SessionTimerService,
    private readonly router: Router,
    private readonly http: HttpClient,
    private readonly clientLogger: ClientLoggerService,
    private readonly tokenService: TokenService
  ) {}

  isLoading(): boolean {
    return this.loader.loading();
  }

  currentMenuItems(): MainMenuItem[] {
    return this.showShellContent() ? this.menuItems : this.footerPageMenuItems;
  }

  showShellContent(): boolean {
    const currentPath = this.router.url.split('?')[0];
    return !this.footerContentRoutes.some((route) => currentPath === route || currentPath.startsWith(`${route}/`));
  }

  ngOnInit(): void {
    this.loader.hide();
    this.loadProfileFromSession();
    this.refreshSessionTimerState();

    this.subscriptions.add(
      this.router.events
        .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
        .subscribe((event) => {
          this.sessionReady = true;
          this.clientLogger.event('AppComponent', 'route-navigation', { url: event.urlAfterRedirects });
          this.loadProfileFromSession();
          this.refreshSessionTimerState();
        })
    );

    this.subscriptions.add(
      this.sessionTimer.sessionExpired$.subscribe(() => {
        this.onLogout();
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.sessionTimer.stopTracking();
  }

  private loadProfileFromSession(): void {
    const payload = this.tokenValidator.getSessionPayload();
    console.log('Session payload loaded in AppComponent:', JSON.stringify(payload, null, 2));
    if (!payload?.profile) return;
    this.tokenService.setAccessToken(payload.token, false);
    this.tokenService.setUserData(payload.profile, false);
    const { firstName, lastName, registeredName, binNumber, taxType, persona } = payload.profile;
    if (firstName || lastName) {
      this.profileName = [firstName, lastName].filter(Boolean).join(' ');
    } else if (registeredName) {
      this.profileName = registeredName;
    }
    if (binNumber) {
      this.profileBin = binNumber;
    }
    if (taxType) {
      this.profileType = taxType;
    } else if (Array.isArray(persona) && persona.length > 0) {
      this.profileType = persona[0];
    }
  }

  onLogout(): void {
    this.sessionTimer.stopTracking();
    try {
      this.tokenValidator.clearSession();
        this.tokenService.clearTokens();
      if (typeof sessionStorage !== 'undefined') {
        sessionStorage.clear();
      }
    } catch {
      // ignore
    }
    try {
      const base = this.otbSignupUrl.trim();
      const separator = base.includes('?') ? '&' : '?';
      window.location.replace(`${base}${separator}dmtt_logout=1`);
    } catch {
      // ignore
    }
  }

  private refreshSessionTimerState(): void {
    if (!this.tokenValidator.hasValidSession()) {
      this.sessionTimer.syncSessionState(false);
      return;
    }
    // Only fetch from backend once — skip if timer is already counting down
    if (!this.sessionTimer.isTimerEnabled()) {
      this.fetchAndApplySessionTimer();
    }
  }

  private fetchAndApplySessionTimer(): void {
    const url = `${environment.apiBaseUrl}/api/v1/auth/session/timer`;
    firstValueFrom(this.http.get<any>(url, { context: new HttpContext().set(SKIP_LOADER, true) }))
      .then((data) => {
        const timerString: string = (data?.response ?? data) as string;
        if (timerString && typeof timerString === 'string' && timerString.includes(':')) {
          const [minutes, seconds] = timerString.split(':').map(Number);
          const totalSeconds = (minutes || 0) * 60 + (seconds || 0);
          if (totalSeconds > 0) {
            this.sessionTimer.startWithSeconds(totalSeconds);
            return;
          }
        }
        this.sessionTimer.syncSessionState(true);
      })
      .catch(() => {
        this.sessionTimer.syncSessionState(true);
      });
  }

}

